import { BaseService } from "../BaseService";
import { TherapistBaseService } from "./TherapistBaseService";
import { AuthService } from "../auth/auth.service";
import { environment } from '../../../../src/environments/environment';
import { HttpClient } from "@angular/common/http";
import { Router } from '@angular/router';
import { Observable, of, throwError } from "rxjs";
import { catchError, concatMap, delay, map, retryWhen, take } from 'rxjs/operators';
import { ApiResult } from "../../models/ApiResult";
import { TherapistModel } from "../../models/api/TherapistModel";
import { TherapistFindResultModel } from "../../models/api/TherapistFindResultModel";

import { Injectable } from "@angular/core";
import { ActiveTherapistModel } from "../../models/api/ActiveTherapistModel";
import { Grid_BookingModel } from "../../models/api/Grid_BookingModel";
import { Grid_Therapist_ExtModel } from "../../models/api/Grid_Therapist_ExtModel";
import { TherapistEventDetailsModel } from "../../models/api/TherapistEventDetails";

@Injectable({
  providedIn: 'root',
})

export class TherapistService extends TherapistBaseService {

    constructor(private h: HttpClient, private r: Router, private a: AuthService) {
        super(h, r, a);
    };

  /// <summary>
  /// GET: Gets a list of active therapists
  /// /Therapist/active
  /// </summary>
  /// <returns></returns>
  public getActiveTherapists(): Observable<ActiveTherapistModel[]> {

    const url = `${super.getBaseUrl() + '/active'}`;

    if (!environment.production)
      console.log("Therapist:Calling " + url);

    return this.h.get<ActiveTherapistModel[]>(url)
      .pipe(
        map(next => {
          for (var i = 0; i < next.length; i++) {
            let o: ActiveTherapistModel = Object.assign(new ActiveTherapistModel(), next[i]);
            next[i] = o;
          }

          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Gets a list of bookings waiting for anyone
  /// /Therapist/anyone
  /// </summary>
  /// <returns></returns>
  public getBookingsWaitingForAnyone(): Observable<Grid_BookingModel[]> {

    const url = `${super.getBaseUrl() + '/anyone'}`;

    if (!environment.production)
      console.log("Therapist:Calling " + url);

    return this.h.get<Grid_BookingModel[]>(url)
      .pipe(
        map(next => {
          for (var i = 0; i < next.length; i++) {
            let o: Grid_BookingModel = Object.assign(new Grid_BookingModel(), next[i]);
            next[i] = o;
          }

          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Gets a list of bookings waiting for logged in therapist
  /// /Therapist/mine
  /// </summary>
  /// <returns></returns>
  public getBookingsWaitingForMe(): Observable<Grid_BookingModel[]> {

    const url = `${super.getBaseUrl() + '/mine'}`;

    if (!environment.production)
      console.log("Therapist:Calling " + url);

    return this.h.get<Grid_BookingModel[]>(url)
      .pipe(
        map(next => {
          for (var i = 0; i < next.length; i++) {
            let o: Grid_BookingModel = Object.assign(new Grid_BookingModel(), next[i]);
            next[i] = o;
          }

          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// PUT: Clocks out a therapist
  /// /Therapist/clockout
  /// </summary>
  /// <returns></returns>
  public clockOut(therapistId: number): Observable<boolean> {

    const url = `${super.getBaseUrl() + '/clockout/' + therapistId}`;

    if (!environment.production)
      console.log("Therapist: Calling " + url);

    return this.h.put<boolean>(url, null)
      .pipe(
        map(next => {
          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Retrieves a list of custom Grid_Therapist_Ext objs
  /// /Therapist/Grid/byevent/5
  /// </summary>
  /// <param name="eventid"></param>
  /// <returns></returns>
  public getByEvent(eventid: number): Observable<Grid_Therapist_ExtModel[]> {

    // Get Single TherapistEvent Record

    const url = `${super.getBaseUrl()}/grid/byevent/${eventid}`;

    if (!environment.production)
      console.log("Get ByEvent: Calling " + url);

    return this.h.get<Grid_Therapist_ExtModel[]>(url)
      .pipe(
        map(next => {
          for (var i = 0; i < next.length; i++) {
            let o: Grid_Therapist_ExtModel = Object.assign(new Grid_Therapist_ExtModel(), next[i]);
            next[i] = o;
          }

          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }

  /// <summary>
  /// GET: Retrieves a list of custom ActiveTherapistModel objs
  /// /Therapist/activebyevent/5
  /// </summary>
  /// <param name="eventid"></param>
  /// <returns></returns>
  public getActiveByEvent(eventid: number): Observable<ActiveTherapistModel[]> {

    // Get Single TherapistEvent Record

    const url = `${super.getBaseUrl()}/activebyevent/${eventid}`;

    if (!environment.production)
      console.log("Get ActiveByEvent: Calling " + url);

    return this.h.get<ActiveTherapistModel[]>(url)
      .pipe(
        map(next => {
          for (var i = 0; i < next.length; i++) {
            let o: ActiveTherapistModel = Object.assign(new ActiveTherapistModel(), next[i]);
            next[i] = o;
          }

          return next;
        }),
        retryWhen(errors =>
          errors.pipe(
            concatMap((error, count) => {
              if (count > this.RETRY_CEILING) return throwError(error);
              return this.tokenCheckAndRefresh(error);
            }),
            delay(1000),
          ),
        ),
        catchError(this.errorHandler)
      );
  }
}
